import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, MessageSquare, Send, Sparkles } from 'lucide-react';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ModuleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Scroll to top when component mounts or module changes
    window.scrollTo(0, 0);
    fetchModule();
  }, [id, user, navigate]);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/modules/${id}`);
      setModule(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du module');
      navigate('/training');
    } finally {
      setLoading(false);
    }
  };

  const handleAskAI = async () => {
    if (!message.trim()) {
      toast.error('Veuillez entrer un message');
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/assistant`, {
        module_id: id,
        message: message
      });
      setAiResponse(response.data.reply);
      toast.success('Réponse reçue!');
    } catch (error) {
      toast.error('L\'assistant IA nécessite une clé OpenAI valide');
      setAiResponse('⚠️ L\'assistant IA nécessite une clé OpenAI configurée dans le backend.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    try {
      await axios.post(`${API}/progress/update`, {
        module_id: id,
        completed: true
      });
      setCompleted(true);
      toast.success('Module marqué comme terminé!');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f0f9ff' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du module...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}>
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <Button 
            data-testid="back-button"
            onClick={() => navigate('/training')} 
            variant="ghost"
            className="gap-2 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au training
          </Button>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0c4a6e' }}>
            {module?.title}
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Module Content */}
          <div className="space-y-6">
            <Card className="border-2">
              <CardHeader>
                <CardTitle style={{ color: '#0c4a6e' }}>Description</CardTitle>
                <CardDescription className="text-base">
                  {module?.description}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Lessons */}
            {module?.lessons && module.lessons.length > 0 && (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle style={{ color: '#0c4a6e' }}>Leçons</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {module.lessons.map((lesson, index) => (
                    <div key={index} className="p-4 rounded-lg" style={{ background: '#f0f9ff' }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#0369a1' }}>
                        {lesson.title}
                      </h4>
                      <p className="text-gray-700">{lesson.content}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Mark Complete */}
            <Card className="border-2">
              <CardContent className="pt-6">
                <Button 
                  data-testid="mark-complete-button"
                  onClick={handleMarkComplete}
                  disabled={completed}
                  className="w-full gap-2"
                  style={{ background: completed ? '#10b981' : '#0284c7' }}
                >
                  {completed ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Module terminé
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Marquer comme terminé
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* AI Assistant */}
          <div className="lg:sticky lg:top-4 h-fit">
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" style={{ color: '#0284c7' }} />
                  <CardTitle style={{ color: '#0c4a6e' }}>Assistant IA</CardTitle>
                </div>
                <CardDescription>
                  Posez vos questions sur ce module
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Textarea
                    data-testid="ai-message-input"
                    placeholder="Comment puis-je améliorer ma communication avec mon équipe?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                  <Button 
                    data-testid="ask-ai-button"
                    onClick={handleAskAI}
                    disabled={aiLoading || !message.trim()}
                    className="w-full gap-2"
                    style={{ background: '#0284c7' }}
                  >
                    {aiLoading ? (
                      'Réflexion en cours...'
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Demander à l'IA
                      </>
                    )}
                  </Button>
                </div>

                {/* AI Response */}
                {aiResponse && (
                  <div 
                    data-testid="ai-response"
                    className="p-4 rounded-lg border-2" 
                    style={{ background: '#f0f9ff', borderColor: '#bae6fd' }}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <MessageSquare className="w-5 h-5 mt-1" style={{ color: '#0284c7' }} />
                      <h4 className="font-semibold" style={{ color: '#0369a1' }}>Réponse de l'assistant</h4>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{aiResponse}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ModuleDetail;